<template>
  <main id="fattura-dettaglio-page" class="page-container">
    <router-link :to="{ name: 'fatturazione' }" class="fd-back">
      <i class="fa-solid fa-arrow-left"></i> Torna alla fatturazione
    </router-link>

    <p v-if="isLoading && !invoice" class="fd-state">Caricamento…</p>
    <p v-else-if="error" class="fd-state fd-state--error">{{ error }}</p>

    <template v-else-if="invoice">
      <header class="fd-header">
        <div>
          <h1>Fattura {{ invoice.numero || "—" }}</h1>
          <p class="fd-hint">{{ invoice.cliente || "—" }}</p>
        </div>
        <div class="fd-actions">
          <button
            v-if="can('fatture', 'C') && puoVerificare(invoice)"
            type="button"
            class="btn-secondary"
            :disabled="verificando"
            @click="verifica"
          >
            <i
              class="fa-solid fa-rotate"
              :class="{ 'fa-spin': verificando }"
            ></i>
            Verifica ora
          </button>
          <button
            v-if="can('fatture', 'C') && invoice.status === 'scartata'"
            type="button"
            class="btn-primary"
            @click="showReinvio = true"
          >
            <i class="fa-solid fa-paper-plane"></i> Correggi e reinvia
          </button>
          <router-link
            v-if="invoice.quoteId"
            class="btn-secondary"
            :to="{ name: 'view-order', params: { quoteId: invoice.quoteId } }"
          >
            Vai all'ordine
          </router-link>
        </div>
      </header>

      <section class="fd-card">
        <h2>Stato</h2>
        <p>
          <span
            class="invoice-badge"
            :class="`invoice-badge--${invoice.status}`"
          >
            {{ etichettaStato(invoice.status) }}
          </span>
        </p>
        <p v-if="invoice.statusMessage" class="fd-note">
          {{ invoice.statusMessage }}
        </p>
        <p v-if="messaggioEsito(invoice.status)" class="fd-note">
          {{ messaggioEsito(invoice.status) }}
        </p>
      </section>

      <section class="fd-card">
        <h2>Invio</h2>
        <dl class="fd-dl">
          <dt>Numero</dt>
          <dd>{{ invoice.numero || "—" }}</dd>
          <dt>Data</dt>
          <dd>{{ formatDate(invoice.date) }}</dd>
          <dt>Tentativo</dt>
          <dd>{{ invoice.attempt ?? "—" }}</dd>
          <dt>File</dt>
          <dd>{{ invoice.filename || "—" }}</dd>
          <dt>Identificativo SdI</dt>
          <dd>{{ invoice.sdiId || "—" }}</dd>
          <dt>Inviata il</dt>
          <dd>{{ formatDateTime(invoice.sentAt) }}</dd>
          <dt>Ultimo esito</dt>
          <dd>{{ formatDateTime(invoice.statusAt) }}</dd>
          <dt>Stato DocuMI</dt>
          <dd>{{ invoice.documiStatus || "—" }}</dd>
        </dl>
      </section>

      <section class="fd-card">
        <h2>Importi</h2>
        <p class="fd-total">{{ formatTotale(invoice.total) }}</p>
        <div v-if="riepilogo.length" class="tbl-wrap">
          <table class="tbl">
            <thead>
              <tr>
                <th>Aliquota</th>
                <th>Natura</th>
                <th>Riferimento normativo</th>
                <th>Imponibile</th>
                <th>Imposta</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(voce, i) in riepilogo" :key="i">
                <td data-label="Aliquota">{{ voce.aliquota }}%</td>
                <td data-label="Natura">{{ voce.natura || "—" }}</td>
                <td data-label="Riferimento">{{ voce.riferimento || "—" }}</td>
                <td data-label="Imponibile">
                  {{ formatTotale(voce.imponibile) }}
                </td>
                <td data-label="Imposta">{{ formatTotale(voce.imposta) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p
          v-if="riepilogo.length && invoice.status === 'scartata'"
          class="fd-note"
        >
          Riepilogo dall'ultimo invio.
        </p>
      </section>

      <section class="fd-card">
        <h2>Pagamento</h2>
        <dl class="fd-dl">
          <dt>Modalità</dt>
          <dd>{{ modalitaPagamento }}</dd>
          <dt>Scadenza</dt>
          <dd>{{ formatDate(invoice.paymentTerms?.scadenza) }}</dd>
        </dl>
      </section>

      <section class="fd-card">
        <h2>Documenti</h2>
        <p v-if="!haDocumenti(invoice)" class="fd-note">
          I documenti sono disponibili quando la fattura è consegnata.
        </p>
        <div v-else class="fd-docs">
          <template v-if="can('fatture', 'V') && haDocumenti(invoice)">
            <button
              type="button"
              class="btn-secondary"
              :disabled="caricandoDocumento"
              @click="visualizza('pdf')"
            >
              <i class="fa-solid fa-file-pdf"></i> Visualizza PDF
            </button>
            <button
              type="button"
              class="btn-secondary"
              :disabled="caricandoDocumento"
              @click="visualizza('xml')"
            >
              <i class="fa-solid fa-file-code"></i> Visualizza XML
            </button>
          </template>
          <template v-if="can('fatture', 'X') && haDocumenti(invoice)">
            <button type="button" class="btn-secondary" @click="scarica('pdf')">
              <i class="fa-solid fa-download"></i> Scarica PDF
            </button>
            <button type="button" class="btn-secondary" @click="scarica('xml')">
              <i class="fa-solid fa-download"></i> Scarica XML
            </button>
          </template>
          <button
            v-if="can('fatture', 'X')"
            type="button"
            class="btn-secondary"
            @click="scarica('ricevute')"
          >
            <i class="fa-solid fa-download"></i> Scarica ricevute SdI
          </button>
        </div>

        <div v-if="documentoAperto" class="fd-viewer">
          <div class="fd-viewer-bar">
            <strong>{{ documentoAperto === "pdf" ? "PDF" : "XML" }}</strong>
            <button
              type="button"
              class="icons-button"
              title="Chiudi"
              @click="chiudiDocumento"
            >
              <i class="fa-solid fa-xmark"></i>
            </button>
          </div>
          <iframe
            v-if="documentoAperto === 'pdf'"
            :src="pdfUrl"
            class="fd-frame"
            title="PDF della fattura"
          ></iframe>
          <iframe
            v-else
            sandbox
            :srcdoc="xmlHtml"
            class="fd-frame"
            title="XML della fattura"
          ></iframe>
        </div>
      </section>

      <!-- [sc-49222] Montata col solo permesso: chiusa non carica nulla, e dopo
           l'emissione deve restare per mostrare l'esito anche se lo stato cambia. -->
      <InvoiceModal
        v-if="can('fatture', 'C')"
        v-model="showReinvio"
        :quote="{ id: invoice.quoteId }"
        @emessa="caricaDettaglio"
      />
    </template>
  </main>
</template>

<script>
import axiosInstance from "@/axiosInstance";
import InvoiceModal from "@/components/InvoiceModal.vue";
import {
  ETICHETTE_STATO,
  MODALITA,
  haDocumenti,
  messaggioEsito,
  puoVerificare,
} from "@/utilities/fatture";
import {
  formatPlatformDate,
  formatPlatformDateTime,
} from "@/utilities/platformFormat";

/**
 * [sc-49222] Dettaglio di una fattura elettronica: stato, invio, importi,
 * pagamento e documenti, con le azioni «Verifica ora» e «Correggi e reinvia».
 * PDF e XML si guardano qui (l'XML già reso in HTML dall'API, in un iframe
 * sandbox senza script); le ricevute SdI si scaricano soltanto.
 */
export default {
  name: "FatturaDettaglioPage",
  components: { InvoiceModal },
  props: { invoiceId: { type: [String, Number], required: true } },
  data() {
    return {
      invoice: null,
      isLoading: true,
      error: "",
      verificando: false,
      showReinvio: false,
      documentoAperto: null,
      pdfUrl: "",
      xmlHtml: "",
      caricandoDocumento: false,
      // [sc-49222] Numero dell'ultima lettura partita: una risposta con un
      // numero diverso arriva fuori ordine (cambio di fattura) e si scarta.
      richiesta: 0,
    };
  },
  computed: {
    can() {
      return this.$store.getters["auth/can"];
    },
    riepilogo() {
      return Array.isArray(this.invoice?.riepilogo)
        ? this.invoice.riepilogo
        : [];
    },
    modalitaPagamento() {
      const m = this.invoice?.paymentTerms?.modalita;
      return MODALITA.find((x) => x.value === m)?.label ?? (m || "—");
    },
  },
  watch: {
    invoiceId() {
      this.showReinvio = false;
      this.chiudiDocumento();
      this.caricandoDocumento = false;
      this.caricaDettaglio();
    },
  },
  async created() {
    await this.caricaDettaglio();
  },
  beforeUnmount() {
    this.revocaPdf();
  },
  methods: {
    haDocumenti,
    messaggioEsito,
    puoVerificare,
    async caricaDettaglio() {
      const mio = ++this.richiesta;
      this.isLoading = true;
      this.error = "";
      try {
        const { data } = await axiosInstance.get(`/invoices/${this.invoiceId}`);
        if (mio !== this.richiesta) return;
        this.invoice = data?.invoice ?? null;
      } catch (err) {
        if (mio !== this.richiesta) return;
        this.invoice = null;
        this.error = err?.response?.data?.message || "Fattura non disponibile.";
      } finally {
        if (mio === this.richiesta) this.isLoading = false;
      }
    },
    async verifica() {
      if (this.verificando) return;
      this.verificando = true;
      try {
        await axiosInstance.post(`/invoices/${this.invoiceId}/verify`);
        // La risposta è una riga ridotta: si rilegge il dettaglio intero.
        await this.caricaDettaglio();
      } catch (err) {
        this.$toast.error(
          err?.response?.data?.message || "Verifica non riuscita."
        );
        // [sc-49222] Con un 409 lo stato è cambiato altrove: si rilegge comunque.
        await this.caricaDettaglio();
      } finally {
        this.verificando = false;
      }
    },
    async visualizza(tipo) {
      if (this.caricandoDocumento) return;
      this.caricandoDocumento = true;
      const partitoDa = this.invoiceId;
      try {
        // Il PDF arriva binario, l'XML già reso in HTML.
        const formato =
          tipo === "pdf" ? { responseType: "blob" } : { responseType: "text" };
        const { data } = await axiosInstance.get(
          `/invoices/${this.invoiceId}/view`,
          { params: { tipo }, ...formato }
        );
        // Cambiata fattura nel frattempo: il documento non è più suo.
        if (partitoDa !== this.invoiceId) return;
        this.revocaPdf();
        if (tipo === "pdf") {
          this.pdfUrl = URL.createObjectURL(
            new Blob([data], { type: "application/pdf" })
          );
        } else {
          this.xmlHtml = String(data);
        }
        this.documentoAperto = tipo;
      } catch (err) {
        const messaggio = await this.messaggioDa(err);
        if (partitoDa !== this.invoiceId) return;
        this.$toast.error(messaggio || "Documento non disponibile.");
      } finally {
        if (partitoDa === this.invoiceId) this.caricandoDocumento = false;
      }
    },
    /**
     * Con `responseType: "blob"` l'errore arriva come Blob, con `"text"` come
     * stringa JSON: in entrambi i casi si cerca il `message` dell'API.
     */
    async messaggioDa(err) {
      let dati = err?.response?.data;
      try {
        if (dati && typeof dati.text === "function") dati = await dati.text();
        if (typeof dati === "string") dati = JSON.parse(dati);
      } catch (e) {
        return null;
      }
      return dati?.message ?? null;
    },
    chiudiDocumento() {
      this.revocaPdf();
      this.documentoAperto = null;
      this.xmlHtml = "";
    },
    revocaPdf() {
      if (this.pdfUrl) URL.revokeObjectURL(this.pdfUrl);
      this.pdfUrl = "";
    },
    async scarica(tipo) {
      try {
        const { data } = await axiosInstance.get(
          `/invoices/${this.invoiceId}/download`,
          { params: { tipo } }
        );
        window.open(data.url, "_blank", "noopener");
      } catch (err) {
        this.$toast.error(
          err?.response?.data?.message || "Download non riuscito."
        );
      }
    },
    etichettaStato(stato) {
      return ETICHETTE_STATO[stato] || stato || "—";
    },
    formatDate(iso) {
      return formatPlatformDate(iso);
    },
    formatDateTime(iso) {
      return formatPlatformDateTime(iso);
    },
    formatTotale(valore) {
      const n = Number(valore);
      if (valore === null || valore === undefined || Number.isNaN(n))
        return "—";
      return n.toLocaleString("it-IT", { style: "currency", currency: "EUR" });
    },
  },
};
</script>

<style scoped>
/* `.page-container` ha `margin: 0 auto`: in una colonna flex il margine
   automatico toglie lo stretch e la pagina prende la larghezza del
   contenuto (cambia coi filtri, si allarga con l'XML). Tutto schermo. */
.page-container {
  width: 100%;
  padding: 24px;
}

.fd-back {
  display: inline-block;
  margin-bottom: 12px;
  font-size: 14px;
}

.fd-state {
  margin: 12px 0;
  color: #555;
  font-size: 14px;
}

.fd-state--error {
  color: #b00020;
}

.fd-header {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}

.fd-header h1 {
  margin: 0 0 4px;
  font-size: 20px;
}

.fd-hint {
  margin: 0;
  color: #555;
  font-size: 14px;
}

.fd-actions,
.fd-docs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.fd-card {
  margin-bottom: 12px;
  padding: 14px 16px;
  border: 0.5px solid var(--color-border, #d4d4d8);
  border-radius: 10px;
  background: var(--color-surface, #fff);
}

.fd-card h2 {
  margin: 0 0 8px;
  font-size: 15px;
}

.fd-note {
  margin: 6px 0 0;
  color: #555;
  font-size: 13px;
}

.fd-total {
  margin: 0 0 8px;
  font-size: 18px;
  font-weight: 600;
}

.fd-dl {
  display: grid;
  grid-template-columns: minmax(8rem, max-content) 1fr;
  gap: 4px 16px;
  margin: 0;
  font-size: 14px;
}

.fd-dl dt {
  color: var(--color-text-muted, #71717a);
}

.fd-dl dd {
  margin: 0;
  overflow-wrap: anywhere;
}

.tbl-wrap {
  max-width: 100%;
  overflow-x: auto;
}

.fd-viewer {
  margin-top: 12px;
}

.fd-viewer-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
}

.fd-frame {
  width: 100%;
  height: 70vh;
  border: 0.5px solid var(--color-border, #d4d4d8);
  border-radius: 8px;
  background: #fff;
}

.invoice-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  background: #f3f4f6;
  color: #4b5563;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.4;
}
.invoice-badge--da_verificare {
  background: #fff7ed;
  color: #c2410c;
}
.invoice-badge--inviata {
  background: #eff6ff;
  color: #1d4ed8;
}
.invoice-badge--consegnata,
.invoice-badge--in_cassetto {
  background: #f0fdf4;
  color: #15803d;
}
.invoice-badge--scartata {
  background: #fef2f2;
  color: #b91c1c;
}

@media (max-width: 700px) {
  .page-container {
    padding: 16px;
  }

  .fd-dl {
    grid-template-columns: 1fr;
  }

  .fd-dl dd {
    margin-bottom: 6px;
  }
}
</style>
